import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Search, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { api } from "../../lib/api";
import { useSite } from "../../lib/auth";
import { Btn } from "./Blocks";
import { InquiryForm } from "../../pages/public/Quote";

export const Reveal = ({ as: Tag = "div", className = "", delay = 0, children, ...p }) => {
  const ref = useRef();
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setOn(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { rootMargin: "0px 0px -10% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${on ? "is-visible" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...p}>{children}</Tag>;
};

export const TourCodeSearch = ({ d }) => {
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const go = async (e) => {
    e.preventDefault();
    const code = q.replace(/\s+/g, "").toUpperCase();
    if (!code) { setMsg("Please enter the tour code from your group leader or invitation."); return; }
    setBusy(true); setMsg("");
    try {
      const { data } = await api.get("/public/find", { params: { q: code } });
      if (data.match?.type === "tour") nav(`/tours/${data.match.code}`);
      else setMsg(`We couldn't find a tour with the code “${code}”. Please check the code on your invitation, or contact us and we'll gladly help.`);
    } catch { setMsg("Something went wrong while searching. Please try again in a moment."); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="home-search" data-testid="tour-code-form" role="search" aria-label="Find your tour by code">
      <label htmlFor="tour-code-input" className="block font-display text-xl font-semibold text-[#0D4B78] sm:text-2xl">{d.search_label || "Already have a tour code?"}</label>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1"><Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input id="tour-code-input" value={q} onChange={(e) => { setQ(e.target.value); setMsg(""); }} placeholder={d.search_placeholder || "Enter your tour code"} autoComplete="off" autoCapitalize="characters" spellCheck="false"
            className="h-12 w-full border border-[#0D4B78]/20 bg-white pl-11 pr-4 text-base tracking-wide text-slate-900 outline-none transition-colors placeholder:tracking-normal placeholder:text-slate-400 focus:border-[#0D4B78] focus-visible:ring-2 focus-visible:ring-[#0D4B78]/30" data-testid="tour-code-input" />
        </div>
        <button className="home-btn home-btn-accent h-12 justify-center" disabled={busy} data-testid="tour-code-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{d.search_button || "Find My Tour"}</button>
      </div>
      <p aria-live="polite" className={`text-sm text-[#8a3d27] ${msg ? "mt-3" : ""}`} data-testid="tour-code-error-message">{msg}</p>
    </form>
  );
};

const useReducedMotion = () => {
  const [r, setR] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setR(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return r;
};

const Slide = ({ s, i, active }) => (
  <picture className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${active ? "opacity-100" : "opacity-0"}`} aria-hidden={!active}>
    {s.image_mobile && <source media="(max-width: 767px)" srcSet={s.image_mobile} />}
    <img src={s.image} alt={s.image_alt || ""} width="2000" height="1250" decoding="async" fetchPriority={i === 0 ? "high" : "low"} loading={i === 0 ? "eager" : "lazy"}
      className={`h-full w-full object-cover ${active ? "hero-zoom" : ""}`} style={{ objectPosition: s.focus || "50% 50%" }} data-testid={`hero-slide-${i}`} />
  </picture>
);

const Ctl = ({ label, onClick, children, testid }) => <button type="button" onClick={onClick} aria-label={label} className="flex h-10 w-10 items-center justify-center rounded-full border border-white/50 text-white transition-colors hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" data-testid={testid}>{children}</button>;

const SlideControls = ({ slides, idx, go, paused, setPaused }) => (
  <div className="flex items-center gap-3" data-testid="hero-slide-controls">
    <span className="hidden min-w-[9rem] text-right text-xs font-medium uppercase tracking-[0.2em] text-white/85 sm:inline" aria-live="polite" data-testid="hero-slide-caption">{slides[idx].caption}</span>
    <Ctl label="Previous photo" onClick={() => go(idx - 1)} testid="hero-prev"><ChevronLeft className="h-4 w-4" /></Ctl>
    <div className="flex items-center gap-1">{slides.map((s, i) => (
      <button key={i} type="button" onClick={() => go(i)} aria-label={`Show photo ${i + 1}: ${s.caption || ""}`} aria-current={i === idx} className="flex h-6 w-6 items-center justify-center rounded-full outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" data-testid={`hero-dot-${i}`}>
        <span className={`block h-1.5 rounded-full bg-white transition-all duration-500 ${i === idx ? "w-6 opacity-100" : "w-1.5 opacity-50"}`} /></button>))}</div>
    <Ctl label="Next photo" onClick={() => go(idx + 1)} testid="hero-next"><ChevronRight className="h-4 w-4" /></Ctl>
    <Ctl label={paused ? "Play slideshow" : "Pause slideshow"} onClick={() => setPaused(!paused)} testid="hero-pause">{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</Ctl>
  </div>
);

export const HomeHero = ({ d }) => {
  const slides = d.slides?.length ? d.slides : [{ image: d.image, image_mobile: d.image_mobile, image_alt: d.image_alt }];
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(reduced);
  const n = slides.length;
  const go = (i) => setIdx(((i % n) + n) % n);
  useEffect(() => { if (reduced) setPaused(true); }, [reduced]);
  useEffect(() => {
    if (paused || n < 2) return undefined;
    const t = setTimeout(() => setIdx((x) => (x + 1) % n), (Number(d.interval) || 6) * 1000);
    return () => clearTimeout(t);
  }, [idx, paused, n, d.interval]);
  return (
  <section className="relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-[#0a2f4d] text-white" data-testid="block-home-hero" aria-roledescription="carousel" aria-label="Travel photography">
    <div className="absolute inset-0 -z-10 overflow-hidden">{slides.map((s, i) => <Slide key={i} s={s} i={i} active={i === idx} />)}</div>
    <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(6,24,40,0.5)_0%,rgba(6,24,40,0.08)_30%,rgba(6,24,40,0.12)_55%,rgba(6,24,40,0.7)_100%)]" />
    <div className="absolute inset-y-0 left-0 -z-10 w-full bg-[linear-gradient(90deg,rgba(6,24,40,0.55)_0%,rgba(6,24,40,0.15)_55%,transparent_80%)]" />
    <div className="container-x flex flex-1 flex-col justify-center pb-8 pt-32 sm:pt-36">
      {d.eyebrow && <p className="hero-in text-xs font-semibold uppercase tracking-[0.3em] text-[#f3c9b8]" style={{ animationDelay: "100ms" }}>{d.eyebrow}</p>}
      <h1 className="hero-in mt-5 max-w-5xl text-5xl font-medium leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.5rem]" style={{ animationDelay: "200ms" }} data-testid="home-hero-heading">{d.heading}</h1>
      {d.text && <p className="hero-in mt-6 max-w-2xl text-base leading-relaxed text-white/90 md:text-lg" style={{ animationDelay: "400ms" }}>{d.text}</p>}
      <div className="hero-in mt-9 flex flex-wrap gap-3" style={{ animationDelay: "550ms" }}>
        {d.primary_label && <Btn to={d.primary_link || "#destinations"} className="home-btn home-btn-accent" testid="hero-cta-explore">{d.primary_label}</Btn>}
        {d.secondary_label && <Btn to={d.secondary_link || "/quote"} className="home-btn home-btn-ghost" testid="hero-cta-plan">{d.secondary_label}</Btn>}
      </div>
    </div>
    <div className="container-x hero-in relative flex flex-col-reverse gap-4 pb-8 sm:pb-12 lg:flex-row lg:items-end lg:justify-between" style={{ animationDelay: "700ms" }}>
      <div className="w-full max-w-2xl bg-[#FDF8EF]/95 p-5 text-slate-900 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] backdrop-blur-md sm:p-7" data-testid="tour-code-panel"><TourCodeSearch d={d} /></div>
      {n > 1 && <div className="self-end"><SlideControls slides={slides} idx={idx} go={go} paused={paused} setPaused={setPaused} /></div>}
    </div>
  </section>
  );
};

const DEST_LAYOUT = ["lg:col-span-7 lg:row-span-2 aspect-[4/5] lg:aspect-auto", "lg:col-span-5 aspect-[4/5] lg:aspect-[5/4]", "lg:col-span-5 aspect-[4/5] lg:aspect-[5/4]", "lg:col-span-12 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/8]"];

const DestCard = ({ it, i }) => (
  <Reveal className={`dest-card group relative block overflow-hidden bg-[#0a2f4d] ${DEST_LAYOUT[i % 4]}`} delay={(i % 2) * 120}>
    <Btn to={it.link || "/destinations"} className="absolute inset-0 block outline-none after:pointer-events-none after:absolute after:inset-0 after:z-10 after:content-[''] focus-visible:after:ring-[3px] focus-visible:after:ring-inset focus-visible:after:ring-[#D1694A]" testid={`destination-card-${(it.title || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <img src={it.image} alt={it.image_alt || it.title} width="1200" height="1500" loading="lazy" decoding="async" className="reveal-img h-full w-full object-cover" style={{ objectPosition: it.focus || "50% 50%" }} />
      <span className="absolute inset-0 bg-gradient-to-t from-[#061828]/80 via-[#061828]/10 to-transparent" />
      <span className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
        <span className="flex items-end justify-between gap-4"><span className="font-display text-4xl font-medium sm:text-5xl">{it.title}</span><ArrowRight className="mb-2 h-5 w-5 shrink-0 transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true" /></span>
        <span className="mt-2 block max-w-md text-sm leading-relaxed text-white/85 sm:text-base">{it.caption}</span>
      </span>
    </Btn>
  </Reveal>
);

export const DestinationShowcase = ({ d }) => (
  <section id="destinations" className="scroll-mt-24 bg-[#FDF8EF] py-20 sm:py-28 lg:py-36" data-testid="block-destination-showcase">
    <div className="container-x">
      <Reveal className="max-w-2xl">{d.eyebrow && <p className="eyebrow">{d.eyebrow}</p>}<h2 className="mt-3 text-4xl font-medium leading-tight tracking-tight text-[#0D4B78] sm:text-5xl lg:text-6xl">{d.heading}</h2></Reveal>
      <div className="mt-12 grid gap-4 sm:gap-6 lg:mt-16 lg:grid-cols-12">{(d.items || []).map((it, i) => <DestCard key={i} it={it} i={i} />)}</div>
    </div>
  </section>
);

export const CruiseFeature = ({ d }) => (
  <section className="bg-[#0a2f4d] py-20 text-white sm:py-28 lg:py-36" data-testid="block-cruise-feature">
    <div className="container-x">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>{d.eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f3c9b8]">{d.eyebrow}</p>}<h2 className="mt-3 text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">{d.heading}</h2></div>
        {d.link_label && <Btn to={d.link || "/cruises"} className="home-link text-white" testid="cruises-explore-link">{d.link_label}<ArrowRight className="h-4 w-4" aria-hidden="true" /></Btn>}
      </Reveal>
      <div className="mt-12 grid gap-14 md:grid-cols-2 md:gap-10 lg:mt-16 lg:gap-16">{(d.items || []).map((it, i) => (
        <Reveal key={i} className={`group ${i % 2 ? "md:mt-28" : ""}`} delay={i * 150}>
          <Btn to={it.link || d.link || "/cruises"} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#D1694A]" testid={`cruise-card-${i}`}>
            <span className="block aspect-[4/5] overflow-hidden sm:aspect-[4/3] md:aspect-[4/5]"><img src={it.image} alt={it.image_alt || it.title} width="1400" height="1750" loading="lazy" decoding="async" className="reveal-img h-full w-full object-cover" /></span>
            <span className="mt-6 flex items-baseline gap-4"><span className="font-display text-sm text-[#f3c9b8]">0{i + 1}</span><span className="font-display text-3xl font-medium sm:text-4xl">{it.title}</span></span>
            <span className="mt-3 block max-w-md pl-8 leading-relaxed text-white/80">{it.caption}</span>
          </Btn>
        </Reveal>))}</div>
    </div>
  </section>
);

export const Intro = ({ d }) => {
  const { site } = useSite();
  return (
    <section className="bg-[#FDF8EF] py-20 sm:py-28 lg:py-36" data-testid="block-intro">
      <div className="container-x grid gap-10 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-5">{site.mark_url && <img src={site.mark_url} alt="" width="64" height="54" className="mb-8 h-12 w-auto" />}<h2 className="text-4xl font-medium leading-[1.08] tracking-tight text-[#0D4B78] sm:text-5xl lg:text-6xl">{d.heading}</h2></Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-7 lg:pt-20" delay={150}>
          <p className="font-display text-2xl leading-snug text-slate-800 sm:text-3xl">{d.text}</p>
          {d.link_label && <Btn to={d.link || "/about"} className="home-link mt-8 text-[#0D4B78]" testid="about-a2z-link">{d.link_label}<ArrowRight className="h-4 w-4" aria-hidden="true" /></Btn>}
        </Reveal>
      </div>
    </section>
  );
};

export const Places = ({ d }) => (
  <section className="bg-[#FDF8EF] py-20 sm:py-28" data-testid="block-places">
    <div className="container-x">
      <Reveal className="max-w-2xl">{d.eyebrow && <p className="eyebrow">{d.eyebrow}</p>}<h2 className="mt-3 text-4xl font-medium leading-tight tracking-tight text-[#0D4B78] sm:text-5xl">{d.heading}</h2>{d.text && <p className="mt-5 text-lg leading-relaxed text-slate-700">{d.text}</p>}</Reveal>
      <div className="mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">{(d.items || []).map((it, i) => (
        <Reveal key={i} delay={(i % 3) * 120} data-testid={`place-${i}`}>
          {it.image && <div className="aspect-[4/3] overflow-hidden bg-[#0a2f4d]"><img src={it.image} alt={it.image_alt || it.title} width="1200" height="900" loading="lazy" decoding="async" className="reveal-img h-full w-full object-cover" /></div>}
          <h3 className="mt-5 font-display text-3xl font-medium text-[#0D4B78]">{it.title}</h3>
          {it.text && <p className="mt-2 leading-relaxed text-slate-700">{it.text}</p>}
        </Reveal>))}</div>
    </div>
  </section>
);

export const InquirySection = ({ d }) => (
  <section className="bg-[#f3efe7] py-20 sm:py-28" data-testid="block-inquiry-form">
    <div className="container-x grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">{d.eyebrow && <p className="eyebrow">{d.eyebrow}</p>}<h2 className="mt-3 text-4xl font-medium leading-tight tracking-tight text-[#0D4B78] sm:text-5xl">{d.heading}</h2>{d.text && <p className="mt-5 text-lg leading-relaxed text-slate-700">{d.text}</p>}</Reveal>
      <div className="bg-white p-6 shadow-sm sm:p-8 lg:col-span-7"><InquiryForm kind={d.kind || "pilgrimage"} /></div>
    </div>
  </section>
);

export const Closing = ({ d }) => (
  <section className="relative isolate overflow-hidden bg-[#0D4B78] py-24 text-center text-white sm:py-32 lg:py-40" data-testid="block-closing">
    {d.image && <img src={d.image} alt="" width="1600" height="1066" loading="lazy" decoding="async" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25" />}
    <Reveal className="container-x max-w-3xl">
      <h2 className="text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">{d.heading}</h2>
      {d.text && <p className="mx-auto mt-6 max-w-xl text-base text-white/85 md:text-lg">{d.text}</p>}
      {d.button_label && <Btn to={d.button_link || "/quote"} className="home-btn home-btn-accent mt-10" testid="closing-cta-button">{d.button_label}</Btn>}
    </Reveal>
  </section>
);
